import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
} from "react-router-dom";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  selectItemsLoading,
  selectItems,
} from "../../features/items/itemSelectors";
import { fetchItems } from "../../features/items/itemThunks";
import { itemService } from "../../api/services/itemService";
import {
  SHIPPING_CUTOFF_METHODS,
  SELECTED_SHIPPING_STORAGE_KEY,
  getSelectedShippingMethodId,
  notifyShippingMethodChanged,
} from "../../utils/shippingCutoffConfig";
import "./App.css";
import CategoryPage from "../CategoryPage/CategoryPage";
import NavbarComponent from "../NavbarComponent/NavbarComponent";
import HomePage from "../HomePage/HomePage";
import ProductDetailInfo from "../ProductDetailInfo/ProductDetailInfo";
import LoginCreation from "../LoginCreation/LoginCreation";
import CreateAccount from "../CreateAccount/CreateAccount";
import ResetPassword from "../ResetPassword/ResetPassword";
import ForgotPassword from "../ForgotPassword/ForgotPassword";
import ConfirmEmail from "../ConfirmEmail/ConfirmEmail";
// import LcdBuy from "../LcdBuy/LcdBuy";
import CartDetails from "../CartDetails/CartDetails";
import Checkout from "../Checkout/Checkout";
import AboutUs from "../About/AboutUs";
// import QuickOrder from "../QuickOrder/QuickOrder";
import TradeMark from "../TradeMark/TradeMark";
import PrivacyPolicy from "../PrivacyPolicy/PrivacyPolicy";
import TermsConditions from "../TermsConditions/TermsConditions";
import PreOwnedDevices from "../PreOwnedDevices/PreOwnedDevices";
import ReturnPolicy from "../ReturnPolicy/ReturnPolicy";
import OrderSuccess from "../OrderSuccess/OrderSuccess";
import MyAccount from "../MyAccount/MyAccount";
import Blog from "../Blog/Blog";
import Footer from "../Footer/Footer";
import Loader from "../Loader/Loader";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import EmailVerificationGate from "../EmailVerificationGate/EmailVerificationGate";

function App() {
  const COOKIE_CONSENT_KEY = "wireox_cookie_consent";
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectItemsLoading);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedShipping, setSelectedShipping] = useState(
    getSelectedShippingMethodId,
  );
  const [showShippingDropdown, setShowShippingDropdown] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    hours: 6,
    minutes: 4,
    seconds: 47,
  });
  const [activeNavItem, setActiveNavItem] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState("domestic");
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  // Search modal state: show when user types, MODEL (3) + PRODUCTS (4)
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchResults, setSearchResults] = useState({
    models: [],
    products: [],
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const navCloseTimeoutRef = useRef(null);
  const searchDebounceRef = useRef(null);
  useAppSelector(selectItems);

  const getImageUrl = useCallback((imagePath) => {
    if (!imagePath) return "";
    if (
      typeof imagePath === "string" &&
      (imagePath.startsWith("http://") || imagePath.startsWith("https://"))
    )
      return imagePath;
    if (typeof imagePath === "string" && imagePath.startsWith("/"))
      return `https://wireox.erp.gold${imagePath}`;
    return imagePath;
  }, []);

  // Debounced search: when searchQuery has 2+ chars, call getitemandmodelwithsearch API and show modal
  useEffect(() => {
    const query = (searchQuery || "").trim();
    if (query.length < 2) {
      setShowSearchModal(false);
      setSearchResults({ models: [], products: [] });
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      return () => {};
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setShowSearchModal(true);
      setSearchLoading(true);
      try {
        const res = await itemService.getItemAndModelWithSearch(query);
        // ModelList: [{ Make, Brand, Model }, ...] -> max 3 for left column, concatenate Make + Brand + Model for display
        const models = (res.ModelList || [])
          .slice(0, 3)
          .map((m) => {
            const label = [m.Make, m.Brand, m.Model].filter(Boolean).join(" ");
            return label ? { label, model: m.Model || "" } : null;
          })
          .filter(Boolean);
        // ItemList: [{ ItemId, ItemName, UnitPrice, DiscountPrice, AvailableQty, SKU, Images }, ...] -> max 4 for product cards
        const products = (res.ItemList || []).slice(0, 4).map((item, index) => {
          let imageUrl = "";
          if (item.Images && item.Images.length > 0) {
            const first = item.Images[0];
            imageUrl = getImageUrl(
              typeof first === "object" ? first.ImagePath : first,
            );
          }
          const unitPrice = item.UnitPrice || 0;
          const discountPrice =
            item.DiscountPrice > 0 ? item.DiscountPrice : null;
          const hasDiscount = discountPrice && unitPrice > discountPrice;
          return {
            id: item.ItemId || index + 1,
            name: item.ItemName || "",
            price: hasDiscount ? discountPrice : unitPrice,
            image: imageUrl,
            sku: item.SKU || "",
            inStock: (item.AvailableQty || 0) > 0,
          };
        });
        setSearchResults({ models, products });
      } catch (err) {
        setSearchResults({ models: [], products: [] });
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, getImageUrl]);

  const closeSearchModal = useCallback(() => {
    setShowSearchModal(false);
    setSearchQuery("");
  }, []);

  // Fetch items data on app mount to populate navbar dropdown
  useEffect(() => {
    dispatch(fetchItems());
  }, [dispatch]);

  // Cookie consent banner: show only if no prior consent is saved.
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      setShowCookieBanner(true);
    }
  }, []);

  const saveCookieConsent = (choice) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, choice);
    setShowCookieBanner(false);
  };

  const shippingMethods = useMemo(() => SHIPPING_CUTOFF_METHODS, []);

  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_SHIPPING_STORAGE_KEY, selectedShipping);
    } catch {
      // Ignore storage write errors.
    }
    notifyShippingMethodChanged(selectedShipping);
  }, [selectedShipping]);

  // Timer effect - updates based on selected shipping method
  useEffect(() => {
    const selectedMethod = shippingMethods.find(
      (method) => method.id === selectedShipping,
    );

    if (selectedMethod) {
      setTimeLeft({
        hours: selectedMethod.hours,
        minutes: selectedMethod.minutes,
        seconds: selectedMethod.seconds,
      });
    }
  }, [selectedShipping, shippingMethods]);

  // Timer countdown effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        let { hours, minutes, seconds } = prev;
        seconds--;

        if (seconds < 0) {
          seconds = 59;
          minutes--;
        }
        if (minutes < 0) {
          minutes = 59;
          hours--;
        }
        if (hours < 0) {
          // Reset to 24 hours when countdown reaches zero
          hours = 23;
          minutes = 59;
          seconds = 59;
        }

        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    closeSearchModal();
    // Submit can navigate to a search results page if you add one later
  };

  const handleNavItemClick = useCallback(
    (navItem, e) => {
      e?.preventDefault?.();

      const clearPendingClose = () => {
        if (navCloseTimeoutRef.current) {
          clearTimeout(navCloseTimeoutRef.current);
          navCloseTimeoutRef.current = null;
        }
      };

      // Explicit close (mobile close button, option select, outside click)
      if (navItem == null) {
        if (activeNavItem) {
          clearPendingClose();
          setIsClosing(true);
          navCloseTimeoutRef.current = setTimeout(() => {
            setActiveNavItem(null);
            setIsClosing(false);
            navCloseTimeoutRef.current = null;
          }, 300);
        }
        return;
      }

      // Same item clicked again: close with animation
      if (activeNavItem === navItem) {
        clearPendingClose();
        setIsClosing(true);
        navCloseTimeoutRef.current = setTimeout(() => {
          setActiveNavItem(null);
          setIsClosing(false);
          navCloseTimeoutRef.current = null;
        }, 300);
      } else {
        clearPendingClose();
        setActiveNavItem(navItem);
        setIsClosing(false);
      }
    },
    [activeNavItem],
  );

  useEffect(() => {
    return () => {
      if (navCloseTimeoutRef.current) {
        clearTimeout(navCloseTimeoutRef.current);
      }
    };
  }, []);

  const handleDropdownOptionClick = (category) => {
    console.log("Dropdown option clicked:", category);
    // Close dropdown
    setIsClosing(true);
    setTimeout(() => {
      setActiveNavItem(null);
      setIsClosing(false);
    }, 300);
    console.log("Opening category page for:", category);
  };

  const selectedMethod = shippingMethods.find(
    (method) => method.id === selectedShipping,
  );

  return (
    <Router>
      <ScrollToTop />
      <EmailVerificationGate />
      <div className="App">
        {/* Loader Overlay */}
        {isLoading && <Loader />}

        {/* Announcement Bar */}
        {/* <AnnouncementBar /> */}

        {/* Navigation Component */}
        <NavbarComponent
          activeNavItem={activeNavItem}
          isClosing={isClosing}
          handleNavItemClick={handleNavItemClick}
          handleDropdownOptionClick={handleDropdownOptionClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          showSearchModal={showSearchModal}
          searchResults={searchResults}
          searchLoading={searchLoading}
          closeSearchModal={closeSearchModal}
          showServicesDropdown={showServicesDropdown}
          setShowServicesDropdown={setShowServicesDropdown}
          timeLeft={timeLeft}
          showShippingDropdown={showShippingDropdown}
          setShowShippingDropdown={setShowShippingDropdown}
          selectedShipping={selectedShipping}
          setSelectedShipping={setSelectedShipping}
          shippingMethods={shippingMethods}
          selectedMethod={selectedMethod}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        {/* Main Content Area */}
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/category/:categoryName" element={<CategoryPage />} />
            <Route path="/product-detail" element={<ProductDetailInfo />} />
            <Route path="/login" element={<LoginCreation />} />
            <Route path="/create-account" element={<CreateAccount />} />
            <Route path="/forget-password" element={<ResetPassword />} />
            <Route path="/forgotpassword" element={<ForgotPassword />} />
            <Route path="/Confirm" element={<ConfirmEmail />} />
            <Route path="/confirm" element={<ConfirmEmail />} />
            {/* <Route path="/lcd-buy" element={<LcdBuy />} /> */}
            <Route path="/cart" element={<CartDetails />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/blog" element={<Blog />} />
            {/* <Route path="/quick-order" element={<QuickOrder />} /> */}
            <Route path="/trademark" element={<TradeMark />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/preowned-devices" element={<PreOwnedDevices />} />
            <Route path="/return-policy" element={<ReturnPolicy />} />
            <Route path="/order-success" element={<OrderSuccess />} />
            <Route path="/my-account" element={<MyAccount />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {showCookieBanner && (
          <div
            className="cookie-consent-banner"
            role="dialog"
            aria-live="polite"
          >
            <div className="cookie-consent-content">
              <p className="cookie-consent-title">
                This website uses cookies to ensure you get the best experience.
              </p>
              <p className="cookie-consent-text">
                Wireox and our selected partners use cookies and similar
                technologies (together, "cookies") that are necessary to provide
                this website and improve your browsing experience.
              </p>
              <p className="cookie-consent-text">
                With your consent, we also use cookies for analytics and
                performance insights.
              </p>
              <p className="cookie-consent-text">
                See our{" "}
                <Link
                  className="cookie-consent-link"
                  to="/privacy-policy"
                  onClick={() => saveCookieConsent("preferences")}
                >
                  Privacy Policy
                </Link>{" "}
                to learn more about how we use cookies.
              </p>
              <p className="cookie-consent-text">
                You can update your cookie choices at any time.
              </p>
            </div>
            <div className="cookie-consent-actions">
              <button
                type="button"
                className="cookie-consent-btn"
                onClick={() => saveCookieConsent("accept_all")}
              >
                Accept all cookies
              </button>
              <button
                type="button"
                className="cookie-consent-btn"
                onClick={() => saveCookieConsent("decline_non_necessary")}
              >
                Decline all non-necessary cookies
              </button>
              <button
                type="button"
                className="cookie-consent-btn"
                onClick={() => saveCookieConsent("preferences")}
              >
                Cookie preferences
              </button>
            </div>
          </div>
        )}

        {/* Footer Component */}
        <Footer />
      </div>
    </Router>
  );
}

export default App;
